// Loan Calculator Math Functions
// Based on legacy calculator implementation

export type CalculationMode = 'pmt' | 'n' | 'i' | 'pv' | 'fv';

export type LoanCalculatorInputs = {
  years: number;
  months: number;
  interestRate: number; // Annual interest rate (e.g., 17.55)
  installment: number;
  fee: number; // Monthly fee (tgb)
  amount: number; // Principal amount without fees (pv_u_gebyr)
  establishmentFee: number; // One-time establishment fee (egb)
  establishmentFeeIncluded: boolean; // Whether egb is included in amount
  futureValue: number; // FV
  calculationMode: CalculationMode;
};

export type LoanCalculatorResults = {
  installment?: number;
  years?: number;
  months?: number;
  interestRate?: number;
  amount?: number;
  futureValue?: number;
  effectiveRate?: number;
  total?: number;
  repaidPercentage?: number;
};

/**
 * Calculate monthly payment (PMT)
 */
const calculatePMT = (n: number, i: number, pv: number, fv: number, k: number): number | null => {
  const s1 = 1;
  const s2 = -1;

  if (i !== 0 && i > -1 && n !== 0) {
    const pmt = Math.round(
      s2 * (((i * (s1 * fv + pv)) / (1 - Math.pow(1 + i, n)) - i * pv) / (1 + k * i)),
    );
    return pmt;
  }
  return null;
};

/**
 * Calculate number of periods (N)
 */
const calculateN = (i: number, pmt: number, pv: number, fv: number, k: number): number | null => {
  const s1 = 1;
  const s2 = -1;

  if (i !== 0 && i > -1) {
    const n1 = s2 * pmt * (1 + k * i) + i * pv;
    const n2 = s2 * pmt * (1 + k * i) - i * s1 * fv;

    if (n2 * n1 > 0) {
      const mnd = Math.log(n2 / n1) / Math.log(1 + i);
      return mnd;
    }
  }
  return null;
};

/**
 * Calculate interest rate (I) using Newton-Raphson method
 */
const calculateI = (n: number, pmt: number, pv: number, fv: number, k: number): number | null => {
  const s1 = 1;
  const s2 = -1;

  if (n === 0) {
    return null;
  }

  if (pmt === 0) {
    if (pv === 0) {
      return null;
    }
    return -1 + Math.pow((-s1 * fv) / pv, 1 / n);
  }

  if (pv + n * s2 * pmt + s1 * fv === 0) {
    return 0;
  }

  let b = 1;
  let c = 1.1;

  while (Math.abs(b - c) > 0.0000001) {
    b = c;
    const d1 = (pv + s2 * pmt * k) * Math.pow(b, n + 1);
    const d2 = (s2 * pmt * (1 - k) - pv) * Math.pow(b, n);
    const d3 = (s1 * fv - s2 * pmt * k) * b;
    const d4 = s2 * pmt * (k - 1) - s1 * fv;
    const d = d1 + d2 + d3 + d4;

    const e1 = (n + 1) * (pv + s2 * pmt * k) * Math.pow(b, n);
    const e2 = n * (s2 * pmt * (1 - k) - pv) * Math.pow(b, n - 1);
    const e3 = s1 * fv - s2 * pmt * k;
    const e = e1 + e2 + e3;

    c = b - d / e;
  }

  return Math.round(1000000 * (c - 1)) / 1000000;
};

/**
 * Calculate principal value (PV)
 */
const calculatePV = (
  n: number,
  i: number,
  pmt: number,
  fv: number,
  k: number,
  egb: number,
  egbIncl: boolean,
): number | null => {
  const s1 = 1;
  const s2 = -1;

  if (i !== 0 && i > -1) {
    if (egbIncl) {
      return Math.round(
        (s1 * fv - (s2 * pmt * (1 + k * i)) / i) * (1 - Math.pow(1 + i, -n)) - s1 * fv - 0,
      );
    } else {
      return Math.round(
        (s1 * fv - (s2 * pmt * (1 + k * i)) / i) * (1 - Math.pow(1 + i, -n)) - s1 * fv - egb,
      );
    }
  }
  return null;
};

/**
 * Calculate future value (FV)
 */
const calculateFV = (n: number, i: number, pmt: number, pv: number, k: number): number | null => {
  const s1 = 1;
  const s2 = -1;

  if (i !== 0 && i > -1) {
    return Math.round(s1 * ((pv + (s2 * pmt * (1 + k * i)) / i) * (1 - Math.pow(1 + i, n)) - pv));
  }
  return null;
};

/**
 * Calculate effective annual interest rate
 */
const calculateEffI = (
  n: number,
  pmt: number,
  tgb: number,
  pv_u_gebyr: number,
  fv: number,
  k: number,
): number | null => {
  const s1 = 1;
  const s2 = -1;

  const pv2 = pv_u_gebyr;
  const fv2 = 0;
  const pmt2 = ((pmt + tgb) * n - fv) / n;

  let b = 1;
  let c = 1.1;

  while (Math.abs(b - c) > 0.0000001) {
    b = c;

    const d1 = (pv2 + s2 * pmt2 * k) * Math.pow(b, n + 1);
    const d2 = (s2 * pmt2 * (1 - k) - pv2) * Math.pow(b, n);
    const d3 = (s1 * fv2 - s2 * pmt2 * k) * b;
    const d4 = s2 * pmt2 * (k - 1) - s1 * fv2;
    const d = d1 + d2 + d3 + d4;

    const e1 = (n + 1) * (pv2 + s2 * pmt2 * k) * Math.pow(b, n);
    const e2 = n * (s2 * pmt2 * (1 - k) - pv2) * Math.pow(b, n - 1);
    const e3 = s1 * fv2 - s2 * pmt2 * k;
    const e = e1 + e2 + e3;

    c = b - d / e;
  }

  c = Math.pow(c, 12);
  return Math.round((c - 1) * 10000) / 100;
};

/**
 * Main loan calculator function
 * Performs calculations based on the selected mode
 */
export const calculateLoan = (inputs: LoanCalculatorInputs): LoanCalculatorResults => {
  const results: LoanCalculatorResults = {};

  // Parse establishment fee (handle percentage)
  let egb = inputs.establishmentFee;
  const egbStr = inputs.establishmentFee.toString();
  if (egbStr.indexOf('%') > 0) {
    const percentValue = parseFloat(egbStr.replace(/[^0-9.]/g, ''));
    egb = Math.floor((inputs.amount * percentValue) / 100);
  }

  // Calculate total months
  let n = inputs.years * 12 + inputs.months;

  // Monthly interest rate (divide annual rate by 1200)
  let i = inputs.interestRate / 1200;

  // Principal value (pv)
  const pv_u_gebyr = inputs.amount;
  let pv = inputs.establishmentFeeIncluded ? pv_u_gebyr : pv_u_gebyr + egb;

  // Payment without monthly fee
  let pmt = inputs.installment - inputs.fee;

  // Future value
  let fv = inputs.futureValue;

  // Payment type: 0 = end of period (ordinary annuity)
  const k = 0;

  // Perform calculation based on mode
  switch (inputs.calculationMode) {
    case 'pmt': {
      pmt = calculatePMT(n, i, pv, fv, k) ?? 0;
      if (pmt !== null) {
        results.installment = pmt + inputs.fee;
      }
      break;
    }

    case 'n': {
      n = calculateN(i, pmt, pv, fv, k) ?? 0;
      if (n !== null && n >= 0) {
        let aar = Math.floor(n / 12);
        let mnd = Math.round(n - aar * 12);

        if (mnd === 12) {
          mnd = 0;
          aar = aar + 1;
        }

        results.years = aar;
        results.months = mnd;
      }
      break;
    }

    case 'i': {
      i = calculateI(n, pmt, pv, fv, k) ?? 0;
      if (i !== null) {
        results.interestRate = Math.round(i * 120000) / 100;
      }
      break;
    }

    case 'pv': {
      pv = calculatePV(n, i, pmt, fv, k, egb, inputs.establishmentFeeIncluded) ?? 0;
      if (pv !== null) {
        results.amount = pv;
      }
      break;
    }

    case 'fv': {
      fv = calculateFV(n, i, pmt, pv, k) ?? 0;
      if (fv !== null) {
        results.futureValue = fv;
      }
      break;
    }
  }

  // Calculate effective rate (always calculated)
  if (n > 0 && pmt > 0) {
    const effRate = calculateEffI(n, pmt, inputs.fee, pv_u_gebyr, fv, k);
    if (effRate !== null) {
      results.effectiveRate = effRate;
    }
  }

  // Calculate total and repaid percentage (always calculated)
  if (n > 0 && pmt > 0 && pv > 0) {
    results.total = Math.round(n * pmt);
    results.repaidPercentage = Math.round((100 * results.total) / pv);
  }

  return results;
};
